import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import PaginationControls from "./PaginationControls";
import InvoiceBadgeWithPopover from './InvoicePopover';
import authService from '../services/authService';

const EmailListView = ({
    title,
    emails,
    onMarkAsReviewed,
    onAddComment,
    onUpdateComment,
    onViewInvoice,
    currentPage,
    totalPages,
    onPageChange,
    defaultExpanded = false
}) => {
    // Initialize detailed view state based on defaultExpanded prop
    const getInitialDetailedState = () => {
        if (defaultExpanded && emails.length > 0) {
            const initial = {};
            emails.forEach(email => { initial[email.id] = true; });
            return initial;
        }
        return {};
    };

    const getInitialExpandedState = () => {
        if (defaultExpanded && emails.length > 0) {
            const initial = {};
            emails.forEach(email => { initial[email.id] = true; });
            return initial;
        }
        return {};
    };

    const [expandedEmails, setExpandedEmails] = useState(getInitialExpandedState);
    const [emailBodies, setEmailBodies] = useState({}); // Cache fetched email bodies
    const [loadingBodies, setLoadingBodies] = useState({}); // Track which bodies are loading
    const [detailedViewEmails, setDetailedViewEmails] = useState(getInitialDetailedState); // Track which emails show detailed info
    const [editingComment, setEditingComment] = useState(null);
    const [editCommentText, setEditCommentText] = useState('');
    const [animatingButtons, setAnimatingButtons] = useState({}); // Track button animations

    // Modal state for full-screen email view
    const [modalEmail, setModalEmail] = useState(null); // Email currently shown in modal
    const [modalViewMode, setModalViewMode] = useState('email'); // 'text' or 'email'

    // Auto-fetch email bodies when defaultExpanded is true
    useEffect(() => {
        if (defaultExpanded && emails.length > 0) {
            emails.forEach(async (email) => {
                // Check is done inside to avoid adding emailBodies to deps (would cause infinite loop)
                setEmailBodies(prev => {
                    if (prev[email.id]) return prev; // Already fetched

                    // Fetch in background
                    setLoadingBodies(loadPrev => ({ ...loadPrev, [email.id]: true }));
                    const token = localStorage.getItem("accessToken");
                    fetch(`${process.env.REACT_APP_API_URL || ''}/api/emails/${email.id}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    })
                        .then(response => response.json())
                        .then(data => {
                            if (data.success && data.email) {
                                setEmailBodies(p => ({ ...p, [email.id]: data.email }));
                            }
                        })
                        .catch(error => console.error('Failed to fetch email body:', error))
                        .finally(() => setLoadingBodies(loadPrev => ({ ...loadPrev, [email.id]: false })));

                    return prev;
                });
            });
        }
    }, [defaultExpanded, emails]);

    // Handle review toggle with animation
    const handleReviewToggle = (emailId, isCurrentlyReviewed) => {
        // Trigger animation
        setAnimatingButtons(prev => ({ ...prev, [emailId]: isCurrentlyReviewed ? 'unreview' : 'review' }));

        // Call the actual handler
        onMarkAsReviewed(emailId);

        // Clear animation after it completes
        setTimeout(() => {
            setAnimatingButtons(prev => ({ ...prev, [emailId]: null }));
        }, 600);
    };

    // Sort emails by date (newest to oldest)
    const sortedEmails = [...emails].sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateB - dateA; // Newest first
    });

    // Toggle between compact and detailed view
    const toggleDetailedView = (emailId) => {
        setDetailedViewEmails(prev => ({
            ...prev,
            [emailId]: !prev[emailId]
        }));
    };

    // Open email in full-screen modal
    const openEmailModal = async (email) => {
        setModalEmail(email);
        setModalViewMode('email'); // Default to email view
        document.body.style.overflow = 'hidden'; // Prevent background scrolling

        // Fetch email body if not cached
        if (!emailBodies[email.id]) {
            setLoadingBodies(prev => ({ ...prev, [email.id]: true }));
            try {
                const token = localStorage.getItem("accessToken");
                const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/emails/${email.id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const data = await response.json();
                if (data.success && data.email) {
                    setEmailBodies(prev => ({ ...prev, [email.id]: data.email }));
                }
            } catch (error) {
                console.error('Failed to fetch email body:', error);
            }
            setLoadingBodies(prev => ({ ...prev, [email.id]: false }));
        }
    };

    // Close the modal
    const closeEmailModal = () => {
        setModalEmail(null);
        document.body.style.overflow = ''; // Restore scrolling
    };

    // Handle escape key to close modal
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && modalEmail) {
                closeEmailModal();
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [modalEmail]);

    // Start editing a comment - strips [Name] prefix for editing
    const handleEditComment = (emailId, commentText) => {
        setEditingComment(emailId);
        // Extract just the content without [Name] prefix for editing
        const parsed = parseComment(commentText);
        setEditCommentText(parsed.content);
    };

    // Parse comment to extract author name and content
    // Format: [Author Name] Comment text here
    const parseComment = (comment) => {
        if (!comment) return { author: null, content: '' };

        // Check if comment starts with [Name] format
        const match = comment.match(/^\[([^\]]+)\]\s*/);
        if (match) {
            return {
                author: match[1],
                content: comment.substring(match[0].length)
            };
        }
        // Old comment without name - return as-is
        return { author: null, content: comment };
    };

    // Cancel editing a comment
    const handleCancelEdit = () => {
        setEditingComment(null);
        setEditCommentText('');
    };

    // Save an edited comment - prepends [Username] to new/edited comments
    const handleSaveEditedComment = (emailId) => {
        if (editCommentText.trim()) {
            const username = authService.getUsername() || 'Unknown';
            // Prepend the username in brackets
            const commentWithName = `[${username}] ${editCommentText.trim()}`;
            onUpdateComment(emailId, commentWithName);
            setEditingComment(null);
            setEditCommentText('');
        }
    };

    // Function to truncate text to a certain length
    // eslint-disable-next-line no-unused-vars
    const truncateText = (text, maxLength = 100) => {
        if (!text) return "No Body";
        
        // Strip HTML tags for the truncated preview
        const strippedText = text.replace(/<[^>]*>?/gm, '');
        
        if (strippedText.length <= maxLength) return strippedText;
        return strippedText.substr(0, maxLength) + '...';
    };

    // Enhanced function to extract HTML content from raw emails
    const extractHtmlFromRawEmail = (email) => {
        try {
            let htmlContent = null;
            
            // Case 1: Check if we have pre-parsed HTML content
            if (email.rawEmailData && email.rawEmailData.html) {
                return email.rawEmailData.html;
            }
            
            // Case 2: Check if body has a proper HTML structure
            if (email.body) {
                // Look for proper HTML tag start
                const htmlStartIndex = email.body.indexOf('<html');
                const bodyStartIndex = email.body.indexOf('<body');
                
                if (htmlStartIndex !== -1) {
                    // Find the matching closing HTML tag
                    const htmlEndIndex = email.body.lastIndexOf('</html>');
                    if (htmlEndIndex !== -1 && htmlEndIndex > htmlStartIndex) {
                        // Extract the complete HTML document
                        return email.body.substring(htmlStartIndex, htmlEndIndex + 7); // +7 to include '</html>'
                    }
                } else if (bodyStartIndex !== -1) {
                    // If no <html> but has <body>, extract from body tag
                    const bodyEndIndex = email.body.lastIndexOf('</body>');
                    if (bodyEndIndex !== -1 && bodyEndIndex > bodyStartIndex) {
                        return `<html><head><meta charset="UTF-8"></head>${email.body.substring(bodyStartIndex, bodyEndIndex + 7)}</html>`;
                    }
                }
                
                // Case 3: Check for MIME multipart with HTML content
                if (email.body.includes('Content-Type: text/html')) {
                    const parts = email.body.split(/--+[^\r\n]+/);
                    for (const part of parts) {
                        if (part.includes('Content-Type: text/html')) {
                            // Find where headers end and content begins
                            const headersEnd = part.indexOf('\r\n\r\n');
                            if (headersEnd !== -1) {
                                htmlContent = part.substring(headersEnd + 4);
                                
                                // Handle quoted-printable encoding
                                if (part.includes('Content-Transfer-Encoding: quoted-printable')) {
                                    htmlContent = decodeQuotedPrintable(htmlContent);
                                }
                                
                                // If the extracted content doesn't have HTML tags, wrap it
                                if (!htmlContent.trim().startsWith('<')) {
                                    htmlContent = `<html><body>${htmlContent}</body></html>`;
                                }
                                
                                return htmlContent;
                            }
                        }
                    }
                }
                
                // Case 4: Look for just HTML-like content with tags
                if (/<[a-z][\s\S]*>/i.test(email.body)) {
                    // If it contains HTML tags but not proper structure, wrap it
                    return `<html><body>${email.body}</body></html>`;
                }
                
                // Case 5: Fallback - convert plain text to simple HTML
                return `<html><body>${email.body.replace(/\n/g, '<br>')}</body></html>`;
            }
            
            return null;
        } catch (error) {
            console.error("Error extracting HTML from email:", error);
            return null;
        }
    };

    // Update the extractEmailContent function to use the enhanced HTML extraction
    const extractEmailContent = (email) => {
        try {
            let htmlContent = extractHtmlFromRawEmail(email);
            let plainTextContent = null;
            
            // Extract plain text
            if (email.rawEmailData && email.rawEmailData.text) {
                plainTextContent = email.rawEmailData.text;
            } else if (email.body) {
                // Try to extract plain text from MIME parts
                if (email.body.includes('Content-Type: text/plain')) {
                    const parts = email.body.split(/--+[^\r\n]+/);
                    for (const part of parts) {
                        if (part.includes('Content-Type: text/plain')) {
                            const headersEnd = part.indexOf('\r\n\r\n');
                            if (headersEnd !== -1) {
                                plainTextContent = part.substring(headersEnd + 4);
                                
                                // Handle quoted-printable encoding
                                if (part.includes('Content-Transfer-Encoding: quoted-printable')) {
                                    plainTextContent = decodeQuotedPrintable(plainTextContent);
                                }
                                
                                break;
                            }
                        }
                    }
                }
                
                // If no plain text was found, strip HTML tags from HTML content
                if (!plainTextContent && htmlContent) {
                    plainTextContent = stripHtmlTags(htmlContent);
                } else if (!plainTextContent) {
                    // Last resort - use body as plain text
                    plainTextContent = email.body;
                }
            }
            
            return {
                html: htmlContent,
                text: plainTextContent || ''
            };
        } catch (error) {
            console.error("Error extracting email content:", error);
            return {
                html: null,
                text: email.body || null
            };
        }
    };

    // Improved helper function to strip HTML tags for plain text view
    const stripHtmlTags = (html) => {
        if (!html) return '';
        
        // Try to use DOM parser first for better results
        try {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            let text = doc.body.textContent || '';
            
            // Clean up whitespace
            return text.replace(/\s+/g, ' ').trim();
        } catch (e) {
            // Fallback to regex-based approach
            return html.replace(/<[^>]*>/g, '')
                      .replace(/&nbsp;/g, ' ')
                      .replace(/&amp;/g, '&')
                      .replace(/&lt;/g, '<')
                      .replace(/&gt;/g, '>')
                      .replace(/&quot;/g, '"')
                      .replace(/\s+/g, ' ')
                      .trim();
        }
    };
    
    // Function to decode quoted-printable content
    const decodeQuotedPrintable = (str) => {
        try {
            // Replace =XX sequences with corresponding characters
            return str.replace(/=([0-9A-F]{2})/gi, (match, p1) => {
                return String.fromCharCode(parseInt(p1, 16));
            })
            // Convert =\r\n to empty string (soft line breaks)
            .replace(/=(?:\r\n?|\n)/g, '');
        } catch (e) {
            console.error("Error decoding quoted-printable:", e);
            return str;
        }
    };

    // Helper to make links clickable in comments
    function linkify(text) {
        if (!text) return '';
        // Regex to match URLs (http, https, www)
        const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
        return text.replace(urlRegex, function(url) {
            let href = url;
            if (!href.match(/^https?:\/\//)) {
                href = 'http://' + href;
            }
            return `<a href="${href}" target="_blank" rel="noopener noreferrer">${url}</a>`;
        });
    }

    // Handle attachment download using base64
    const handleDownloadAttachment = (attachment) => {
        try {
            console.log('📂 handleDownloadAttachment: Received attachment:', attachment);

            // Check if the attachment has base64 data
            if (!attachment.base64) {
                console.error('❌ handleDownloadAttachment: Attachment is missing base64 data:', attachment);
                alert('This attachment cannot be downloaded. Missing base64 data.');
                return;
            }

            console.log('📂 handleDownloadAttachment: Preparing download for attachment:', attachment.filename);

            // Determine the actual content type
            // Priority: 1) Use contentType from backend if it's a valid MIME type
            //           2) Fall back to filename extension detection for legacy data or generic types
            let contentType = attachment.contentType;

            // If contentType is missing, generic disposition type, or invalid, detect from filename
            const invalidContentTypes = ['ATTACHMENT', 'INLINE', 'attachment', 'inline', 'application', 'APPLICATION'];
            if (!contentType || invalidContentTypes.includes(contentType) || !contentType.includes('/')) {
                // Detect common file extensions and assign proper MIME types
                const fileExt = attachment.filename.split('.').pop().toLowerCase();
                const mimeTypes = {
                    'pdf': 'application/pdf',
                    'jpg': 'image/jpeg',
                    'jpeg': 'image/jpeg',
                    'png': 'image/png',
                    'gif': 'image/gif',
                    'svg': 'image/svg+xml',
                    'webp': 'image/webp',
                    'txt': 'text/plain',
                    'csv': 'text/csv',
                    'html': 'text/html',
                    'htm': 'text/html',
                    'doc': 'application/msword',
                    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    'xls': 'application/vnd.ms-excel',
                    'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    'ppt': 'application/vnd.ms-powerpoint',
                    'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                    'mp4': 'video/mp4',
                    'mp3': 'audio/mpeg',
                    'wav': 'audio/wav',
                    'json': 'application/json',
                    'xml': 'application/xml'
                };
                contentType = mimeTypes[fileExt] || 'application/octet-stream';
                console.log(`📂 handleDownloadAttachment: Determined content type as ${contentType} from extension ${fileExt} (original was: ${attachment.contentType})`);
            } else {
                console.log(`📂 handleDownloadAttachment: Using content type from backend: ${contentType}`);
            }

            // Ensure the base64 data has the proper data URL prefix
            let downloadUrl = attachment.base64;

            // If base64 doesn't start with "data:", add the appropriate prefix
            if (!downloadUrl.startsWith('data:')) {
                downloadUrl = `data:${contentType};base64,${attachment.base64}`;
                console.log('📂 handleDownloadAttachment: Added data URL prefix for:', contentType);
            }

            console.log('📂 handleDownloadAttachment: Download URL created:', downloadUrl.substring(0, 50) + '...');

            // Create a download link from the base64 data
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = attachment.filename || 'attachment';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            console.log('✅ handleDownloadAttachment: Download initiated for:', attachment.filename);
        } catch (error) {
            console.error('❌ handleDownloadAttachment: Error downloading attachment:', error);
            alert('Failed to download attachment. See console for details.');
        }
    };

    // Handle attachment preview using base64
    const handlePreviewAttachment = (attachment) => {
        try {
            console.log('📂 handlePreviewAttachment: Received attachment:', attachment);

            // Check if the attachment has base64 data
            if (!attachment.base64) {
                console.error('❌ handlePreviewAttachment: Attachment is missing base64 data:', attachment);
                alert('This attachment cannot be previewed. Missing base64 data.');
                return;
            }

            console.log('📂 handlePreviewAttachment: Preparing preview for attachment:', attachment.filename);

            // Determine the actual content type
            // Priority: 1) Use contentType from backend if it's a valid MIME type
            //           2) Fall back to filename extension detection for legacy data or generic types
            let contentType = attachment.contentType;

            // If contentType is missing, generic disposition type, or invalid, detect from filename
            const invalidContentTypes = ['ATTACHMENT', 'INLINE', 'attachment', 'inline', 'application', 'APPLICATION'];
            if (!contentType || invalidContentTypes.includes(contentType) || !contentType.includes('/')) {
                // Detect common file extensions and assign proper MIME types
                const fileExt = attachment.filename.split('.').pop().toLowerCase();
                const mimeTypes = {
                    'pdf': 'application/pdf',
                    'jpg': 'image/jpeg',
                    'jpeg': 'image/jpeg',
                    'png': 'image/png',
                    'gif': 'image/gif',
                    'svg': 'image/svg+xml',
                    'webp': 'image/webp',
                    'txt': 'text/plain',
                    'csv': 'text/csv',
                    'html': 'text/html',
                    'htm': 'text/html',
                    'doc': 'application/msword',
                    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    'xls': 'application/vnd.ms-excel',
                    'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    'ppt': 'application/vnd.ms-powerpoint',
                    'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                    'mp4': 'video/mp4',
                    'mp3': 'audio/mpeg',
                    'wav': 'audio/wav',
                    'json': 'application/json',
                    'xml': 'application/xml'
                };
                contentType = mimeTypes[fileExt] || 'application/octet-stream';
                console.log(`📂 handlePreviewAttachment: Determined content type as ${contentType} from extension ${fileExt} (original was: ${attachment.contentType})`);
            } else {
                console.log(`📂 handlePreviewAttachment: Using content type from backend: ${contentType}`);
            }

            // Ensure the base64 data has the proper data URL prefix
            let previewData = attachment.base64;
            
            // If base64 doesn't start with 'data:', add the appropriate prefix
            if (!previewData.startsWith('data:')) {
                previewData = `data:${contentType};base64,${attachment.base64}`;
                console.log('📂 handlePreviewAttachment: Added data URL prefix');
            }

            // Open the preview in a new tab
            const previewWindow = window.open('', '_blank');
            const htmlContent = generateUniversalPreviewHtml(
                attachment.filename,
                contentType,
                previewData
            );
            
            previewWindow.document.open();
            previewWindow.document.write(htmlContent);
            previewWindow.document.close();

            console.log('✅ handlePreviewAttachment: Preview opened for:', attachment.filename);
        } catch (error) {
            console.error('❌ handlePreviewAttachment: Error preparing attachment preview:', error);
            alert('Failed to preview attachment. See console for details.');
        }
    };

    // Generate a universal preview HTML that can handle multiple file types
    const generateUniversalPreviewHtml = (filename, contentType, base64Data) => {
        // Group file types by their rendering approach
        const isPdf = contentType === 'application/pdf';
        const isImage = contentType.startsWith('image/');
        const isVideo = contentType.startsWith('video/');
        const isAudio = contentType.startsWith('audio/');
        const isText = contentType.startsWith('text/');
        const isOffice = /word|excel|powerpoint|openxmlformats|ms-|vnd\./.test(contentType);
        
        // Create a unique identifier for this preview session
        const uniqueId = Date.now().toString(36) + Math.random().toString(36).substring(2);
        
        return `
            <!DOCTYPE html>
            <html lang="en">
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>Preview: ${filename}</title>
                <style>
                    body {
                        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
                        margin: 0;
                        padding: 0;
                        display: flex;
                        flex-direction: column;
                        height: 100vh;
                        color: #333;
                        background-color: #f8f9fa;
                    }
                    
                    .header {
                        background-color: #f1f1f1;
                        padding: 15px 20px;
                        border-bottom: 1px solid #ddd;
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        box-shadow: 0 2px 5px rgba(0,0,0,0.05);
                    }
                    
                    .header h2 {
                        margin: 0;
                        font-size: 18px;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                        max-width: 70%;
                    }
                    
                    .actions {
                        display: flex;
                        gap: 10px;
                    }
                    
                    .btn {
                        padding: 8px 16px;
                        border: none;
                        border-radius: 4px;
                        cursor: pointer;
                        font-weight: 500;
                        transition: all 0.2s ease;
                    }
                    
                    .download-btn {
                        background-color: #007bff;
                        color: white;
                    }
                    
                    .download-btn:hover {
                        background-color: #0069d9;
                    }
                    
                    .close-btn {
                        background-color: #6c757d;
                        color: white;
                    }
                    
                    .close-btn:hover {
                        background-color: #5a6268;
                    }
                    
                    .content {
                        flex: 1;
                        overflow: auto;
                        padding: 0;
                        margin: 0;
                        display: flex;
                        justify-content: center;
                        align-items: flex-start;
                        background-color: #e9ecef;
                    }
                    
                    .content-inner {
                        max-height: 100%;
                        max-width: 100%;
                        background-color: white;
                        box-shadow: 0 0 20px rgba(0,0,0,0.1);
                        overflow: auto;
                    }
                    
                    /* File type specific styles */
                    .pdf-container {
                        width: 100%;
                        height: 100%;
                    }
                    
                    iframe.pdf-viewer {
                        width: 100%;
                        height: 100%;
                        border: none;
                    }
                    
                    .image-content {
                        max-width: 100%;
                        max-height: 80vh;
                        object-fit: contain;
                        display: block;
                        margin: 0 auto;
                    }
                    
                    .text-content {
                        white-space: pre-wrap;
                        font-family: monospace;
                        padding: 20px;
                        line-height: 1.5;
                        overflow: auto;
                        max-width: 100%;
                        max-height: 100%;
                        margin: 0;
                    }
                    
                    .video-container, .audio-container {
                        width: 100%;
                        max-width: 800px;
                        margin: 20px auto;
                        background: #000;
                        border-radius: 4px;
                        overflow: hidden;
                    }
                    
                    video, audio {
                        width: 100%;
                        display: block;
                    }
                    
                    .office-viewer, .generic-viewer {
                        width: 100%;
                        height: 80vh;
                        border: none;
                    }
                    
                    .unsupported {
                        padding: 40px;
                        text-align: center;
                        max-width: 600px;
                        margin: 40px auto;
                        background-color: white;
                        border-radius: 8px;
                        box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                    }
                    
                    .unsupported h3 {
                        color: #555;
                        margin-top: 0;
                    }
                    
                    .unsupported p {
                        color: #666;
                        margin-bottom: 20px;
                    }
                    
                    .data-status {
                        position: fixed;
                        bottom: 0;
                        left: 0;
                        right: 0;
                        background: rgba(0,0,0,0.8);
                        color: #4CAF50;
                        padding: 5px;
                        font-family: monospace;
                        font-size: 12px;
                        text-align: center;
                    }
                    
                    .file-icon {
                        font-size: 64px;
                        margin-bottom: 15px;
                        color: #6c757d;
                    }
                </style>
            </head>
            <body>
                <div class="header">
                    <h2>${filename}</h2>
                    <div class="actions">
                        <button class="btn download-btn" onclick="downloadFile()">Download</button>
                        <button class="btn close-btn" onclick="window.close()">Close</button>
                    </div>
                </div>
                
                <div class="content">
                    <div class="content-inner">
                        ${isPdf ? `
                            <style>
                                body {
                                    overflow: hidden;
                                }
                                .header {
                                    position: fixed;
                                    top: 10px;
                                    right: 10px;
                                    z-index: 1000;
                                    background: rgba(241, 241, 241, 0.8);
                                    border-radius: 8px;
                                    box-shadow: 0 2px 10px rgba(0,0,0,0.2);
                                    padding: 10px;
                                    border: none;
                                    max-width: 300px;
                                }
                                .content, .content-inner {
                                    margin: 0;
                                    padding: 0;
                                    position: absolute;
                                    top: 0;
                                    left: 0;
                                    right: 0;
                                    bottom: 0;
                                    background: none;
                                    box-shadow: none;
                                    overflow: hidden;
                                }
                                .pdf-container {
                                    width: 100%;
                                    height: 100%;
                                    position: absolute;
                                    top: 0;
                                    left: 0;
                                    right: 0;
                                    bottom: 0;
                                }
                                iframe.pdf-viewer {
                                    width: 100%;
                                    height: 100vh;
                                    border: none;
                                    margin: 0;
                                    padding: 0;
                                    position: absolute;
                                    top: 0;
                                    left: 0;
                                }
                                .data-status {
                                    display: none;
                                }
                            </style>
                            <div class="pdf-container">
                                <iframe class="pdf-viewer" src="" id="pdf-iframe" title="PDF Viewer"></iframe>
                            </div>
                        ` : isImage ? `
                            <img class="image-content" src="${base64Data}" alt="${filename}">
                        ` : isVideo ? `
                            <div class="video-container">
                                <video controls autoplay>
                                    <source src="${base64Data}" type="${contentType}">
                                    Your browser does not support the video tag.
                                </video>
                            </div>
                        ` : isAudio ? `
                            <div class="audio-container">
                                <audio controls autoplay>
                                    <source src="${base64Data}" type="${contentType}">
                                    Your browser does not support the audio tag.
                                </audio>
                            </div>
                        ` : isText ? `
                            <pre class="text-content" id="text-content">Loading text content...</pre>
                        ` : isOffice ? `
                            <div class="unsupported">
                                <div class="file-icon">📄</div>
                                <h3>Office Document</h3>
                                <p>This document type can't be previewed directly in the browser.</p>
                                <button class="btn download-btn" onclick="downloadFile()">Download File</button>
                            </div>
                        ` : `
                            <div class="unsupported">
                                <div class="file-icon">📎</div>
                                <h3>File Preview Unavailable</h3>
                                <p>This file type (${contentType}) cannot be previewed directly in the browser.</p>
                                <button class="btn download-btn" onclick="downloadFile()">Download File</button>
                            </div>
                        `}
                    </div>
                </div>
                
                <div class="data-status">Preview ID: ${uniqueId} | Type: ${contentType}</div>
                
                <script>
                    // Process different types of content
                    function initializePreview() {
                        console.log("📂 Initializing attachment preview");
                        
                        ${isPdf ? `
                            // Process PDF content
                            processPdfData();
                        ` : ''}
                        
                        ${isText ? `
                            // Process text content
                            processTextData();
                        ` : ''}
                    }
                    
                    // Process PDF data for viewing
                    function processPdfData() {
                        try {
                            console.log("📄 Processing PDF data...");
                            
                            // Strip Base64 metadata if it exists
                            const base64Data = "${base64Data}";
                            const base64WithoutPrefix = base64Data.startsWith("data:application/pdf") 
                                ? base64Data.split(",")[1] 
                                : base64Data.replace(/^data:.*?;base64,/, "");
                            
                            // Convert Base64 to Binary Data
                            const binaryData = atob(base64WithoutPrefix);
                            const byteNumbers = new Uint8Array(binaryData.length);
                            
                            for (let i = 0; i < binaryData.length; i++) {
                                byteNumbers[i] = binaryData.charCodeAt(i);
                            }
                            
                            // Create Blob URL
                            const blob = new Blob([byteNumbers], { type: "application/pdf" });
                            const blobUrl = URL.createObjectURL(blob);
                            
                            // Set the iframe src to the blob URL
                            const pdfIframe = document.getElementById('pdf-iframe');
                            if (pdfIframe) {
                                pdfIframe.src = blobUrl;
                                console.log("✅ PDF Blob URL created and set to iframe");
                            }
                            
                            // Store the blob URL for cleanup
                            window.pdfBlobUrl = blobUrl;
                        } catch (error) {
                            console.error("❌ Failed to process PDF:", error);
                            document.querySelector('.pdf-container').innerHTML = 
                                '<div class="unsupported"><h3>PDF Preview Failed</h3><p>There was an error processing this PDF.</p></div>';
                        }
                    }
                    
                    // Process text data for viewing
                    function processTextData() {
                        try {
                            console.log("📝 Processing text data...");
                            
                            // Get the base64 data and extract the text content
                            const base64Data = "${base64Data}";
                            const base64WithoutPrefix = base64Data.split(",")[1];
                            const textContent = atob(base64WithoutPrefix);
                            
                            // Display the text content
                            document.getElementById('text-content').textContent = textContent;
                            console.log("✅ Text content displayed successfully");
                        } catch (error) {
                            console.error("❌ Failed to process text:", error);
                            document.getElementById('text-content').textContent = "Error processing text content";
                        }
                    }
                    
                    // Universal download function for any file type
                    function downloadFile() {
                        try {
                            console.log("📂 Downloading file...");
                            
                            // Create a blob from the base64 data
                            const base64Data = "${base64Data}";
                            
                            // If we're dealing with a PDF that already has a blob URL
                            if (${isPdf} && window.pdfBlobUrl) {
                                const link = document.createElement('a');
                                link.href = window.pdfBlobUrl;
                                link.download = "${filename}";
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                console.log("✅ Download initiated using existing PDF blob URL");
                                return;
                            }
                            
                            // For other file types
                            const link = document.createElement('a');
                            link.href = base64Data;
                            link.download = "${filename}";
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            
                            console.log("✅ Download initiated");
                        } catch (error) {
                            console.error("❌ Failed to download file:", error);
                            alert('Download failed. Please try again.');
                        }
                    }
                    
                    // Initialize the preview
                    document.addEventListener('DOMContentLoaded', initializePreview);
                    
                    // Cleanup function for any created blob URLs
                    window.addEventListener('beforeunload', function() {
                        if (window.pdfBlobUrl) {
                            URL.revokeObjectURL(window.pdfBlobUrl);
                            console.log("🧹 Cleaned up PDF Blob URL");
                        }
                    });
                </script>
            </body>
            </html>
        `;
    };

    return (
        <div className="list-view">
            <h3>{title}</h3>
            <ul className="email-list">
                {sortedEmails && sortedEmails.length > 0 ? (
                    sortedEmails.map((email) => (
                        <li key={email.id} className={`email-list-item ${email.isReviewed ? 'reviewed-item' : 'unreviewed-item'} ${email.Invoices?.length > 0 ? 'has-invoice' : ''}`}>
                            {/* Compact view - just the subject line and review status */}
                            <div
                                className="email-subject-line"
                                onClick={() => toggleDetailedView(email.id)}
                                role="button"
                                tabIndex={0}
                                aria-expanded={detailedViewEmails[email.id] || false}
                                onKeyDown={(e) => e.key === 'Enter' && toggleDetailedView(email.id)}
                            >
                                <div className="email-subject">
                                    <span className={`expand-arrow ${detailedViewEmails[email.id] ? 'expanded' : ''}`}>
                                        {detailedViewEmails[email.id] ? '▼' : '▶'}
                                    </span>
                                    <strong>{email.subject || "No Subject"}</strong>
                                    {/* Invoice badge with popover for multiple invoices */}
                                    {email.Invoices?.length > 0 && (
                                        <InvoiceBadgeWithPopover
                                            invoices={email.Invoices}
                                            onViewInvoice={onViewInvoice}
                                        />
                                    )}
                                    {/* Attachment indicator/banner for collapsed view */}
                                    {!detailedViewEmails[email.id] && email.attachments?.length > 0 && !email.Invoices?.length && (
                                        <span className="attachment-badge">
                                            <span className="attachment-badge-icon" role="img" aria-label="attachment">📎</span>
                                            {email.attachments.length}
                                        </span>
                                    )}
                                </div>
                                <div className="quick-actions">
                                    <button
                                        className={`review-toggle-btn ${email.isReviewed ? 'mark-unreviewed' : 'mark-reviewed'} ${animatingButtons[email.id] === 'unreview' ? 'animate-unreview' : ''} ${animatingButtons[email.id] === 'review' ? 'animate-review' : ''}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleReviewToggle(email.id, email.isReviewed);
                                        }}
                                        title={email.isReviewed ? "Mark as unreviewed" : "Mark as reviewed"}
                                        aria-label={email.isReviewed ? "Mark task as unreviewed" : "Mark task as reviewed"}
                                    >
                                        <span className={`btn-icon ${animatingButtons[email.id] === 'unreview' ? 'animate-x' : ''}`}>
                                            {animatingButtons[email.id] === 'unreview' ? '✕' : (email.isReviewed ? '✓' : '○')}
                                        </span>
                                    </button>
                                </div>
                            </div>
                            
                            {/* Detailed view - all email info and content */}
                            {detailedViewEmails[email.id] && (
                                <div className="task-detail task-detail-animated">
                                    {/* Email Section */}
                                    <div className="email-detail-section">
                                        <div className="section-header">
                                            <div className="section-icon email-icon">
                                                <span>📧</span>
                                            </div>
                                            <h4 className="section-title">Email Details</h4>
                                        </div>
                                        <div className="email-info-grid">
                                            <div className="email-info-item">
                                                <span className="info-label">From</span>
                                                <span className="info-value">{email.senderEmail || "Unknown Sender"}</span>
                                            </div>
                                            <div className="email-info-item">
                                                <span className="info-label">Sent</span>
                                                <span className="info-value">{email.createdAt ? new Date(email.createdAt).toLocaleString() : "Unknown Date"}</span>
                                            </div>
                                        </div>

                                        {/* Email Content - Open in Modal */}
                                        <div className="email-content-wrapper">
                                            <button
                                                className="detail-btn primary"
                                                onClick={() => openEmailModal(email)}
                                            >
                                                <span className="btn-icon">📄</span>
                                                View Full Email
                                            </button>
                                        </div>
                                    </div>

                                    {/* Attachments Section */}
                                    <div className="email-detail-section">
                                        <div className="section-header">
                                            <div className="section-icon attachment-icon">
                                                <span>📎</span>
                                            </div>
                                            <h4 className="section-title">Attachments</h4>
                                            <span className="section-badge">{email.attachments?.length || 0}</span>
                                        </div>
                                        {email.attachments?.length > 0 ? (
                                            <div className="attachment-list-modern">
                                                {email.attachments.map((attachment, idx) => {
                                                    const fileExt = (attachment.filename || '').split('.').pop().toLowerCase();
                                                    const isPdf = fileExt === 'pdf';
                                                    const fileIcon = isPdf ? '📑' :
                                                        ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExt) ? '🖼️' :
                                                        ['doc', 'docx'].includes(fileExt) ? '📝' :
                                                        ['xls', 'xlsx'].includes(fileExt) ? '📊' : '📄';

                                                    return (
                                                        <div key={idx} className="attachment-card">
                                                            <div className="attachment-file-icon">{fileIcon}</div>
                                                            <div className="attachment-info">
                                                                <span className="attachment-name">{attachment.filename || "Unnamed File"}</span>
                                                                <span className="attachment-size">{(attachment.size / 1024).toFixed(2)} KB</span>
                                                            </div>
                                                            <div className="attachment-actions">
                                                                <button
                                                                    className="attachment-btn download"
                                                                    onClick={() => handleDownloadAttachment(attachment)}
                                                                    title="Download"
                                                                >
                                                                    ⬇️
                                                                </button>
                                                                {isPdf && (
                                                                    <button
                                                                        className="attachment-btn preview"
                                                                        onClick={() => handlePreviewAttachment(attachment)}
                                                                        title="Preview"
                                                                    >
                                                                        👁️
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <div className="empty-attachments">
                                                <span className="empty-icon">📭</span>
                                                <span>No attachments</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Comments Section */}
                                    <div className="email-detail-section">
                                        <div className="section-header">
                                            <div className="section-icon comment-icon">
                                                <span>💬</span>
                                            </div>
                                            <h4 className="section-title">Notes & Comments</h4>
                                        </div>
                                        {editingComment === email.id ? (
                                            <div className="comment-edit-form">
                                                <textarea
                                                    className="comment-textarea"
                                                    value={editCommentText}
                                                    onChange={(e) => setEditCommentText(e.target.value)}
                                                    placeholder="Add your notes here..."
                                                    rows={4}
                                                />
                                                <div className="comment-form-actions">
                                                    <button
                                                        className="detail-btn primary"
                                                        onClick={() => handleSaveEditedComment(email.id)}
                                                    >
                                                        <span className="btn-icon">✓</span>
                                                        Save
                                                    </button>
                                                    <button
                                                        className="detail-btn secondary"
                                                        onClick={handleCancelEdit}
                                                    >
                                                        <span className="btn-icon">✕</span>
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="comment-container">
                                                {email.comment ? (
                                                    (() => {
                                                        const parsed = parseComment(email.comment);
                                                        return (
                                                            <>
                                                                {parsed.author && (
                                                                    <div className="comment-author">
                                                                        <span className="author-icon">👤</span>
                                                                        <span className="author-name">{parsed.author}</span>
                                                                    </div>
                                                                )}
                                                                <div
                                                                    className="comment-display"
                                                                    dangerouslySetInnerHTML={{
                                                                        __html: linkify(parsed.content)
                                                                    }}
                                                                />
                                                            </>
                                                        );
                                                    })()
                                                ) : (
                                                    <div className="comment-display empty">
                                                        No notes added yet
                                                    </div>
                                                )}
                                                <button
                                                    className="detail-btn secondary"
                                                    onClick={() => handleEditComment(email.id, email.comment || "")}
                                                >
                                                    <span className="btn-icon">{email.comment ? '✏️' : '➕'}</span>
                                                    {email.comment ? "Edit Notes" : "Add Notes"}
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {/* Details/Metadata Section */}
                                    <div className="email-detail-section">
                                        <div className="section-header">
                                            <div className="section-icon details-icon">
                                                <span>ℹ️</span>
                                            </div>
                                            <h4 className="section-title">Task Info</h4>
                                        </div>
                                        <div className="details-grid">
                                            <div className="detail-pill">
                                                <span className="pill-icon">📅</span>
                                                <span className="pill-label">Created</span>
                                                <span className="pill-value">{email.createdAt ? new Date(email.createdAt).toLocaleDateString() : "Unknown"}</span>
                                            </div>
                                            <div className="detail-pill">
                                                <span className="pill-icon">🔄</span>
                                                <span className="pill-label">Updated</span>
                                                <span className="pill-value">{email.updatedAt ? new Date(email.updatedAt).toLocaleDateString() : "Unknown"}</span>
                                            </div>
                                            {email.status && (
                                                <div className={`detail-pill status-${email.status.toLowerCase()}`}>
                                                    <span className="pill-icon">{email.status === 'Reviewed' ? '✅' : '⏳'}</span>
                                                    <span className="pill-label">Status</span>
                                                    <span className="pill-value">{email.status}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </li>
                    ))
                ) : (
                    <li className="empty-state">
                        <div className="empty-state-icon">📋</div>
                        <div className="empty-state-title">No tasks to display</div>
                        <div className="empty-state-description">
                            {currentPage > 1
                                ? "No tasks on this page. Try going back to the first page."
                                : "Tasks will appear here once they are available for the selected client."
                            }
                        </div>
                        {currentPage > 1 && (
                            <button
                                className="empty-state-action"
                                onClick={() => onPageChange(1)}
                            >
                                Go to Page 1
                            </button>
                        )}
                    </li>
                )}
            </ul>
            
            {/* Only show pagination if there are emails OR we need to go back to previous page */}
            {(sortedEmails.length > 0 || currentPage > 1) && (
                <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={onPageChange}
                />
            )}

            {/* Full-Screen Email Modal */}
            {modalEmail && (
                <div className="email-modal-overlay" onClick={closeEmailModal}>
                    <div className="email-modal" onClick={(e) => e.stopPropagation()}>
                        {/* Modal Header */}
                        <div className="email-modal-header">
                            <div className="email-modal-title">
                                <span className="modal-icon">📧</span>
                                <h3>{modalEmail.subject || "No Subject"}</h3>
                            </div>
                            <button
                                className="email-modal-close"
                                onClick={closeEmailModal}
                                aria-label="Close modal"
                            >
                                ✕
                            </button>
                        </div>

                        {/* Modal Meta Info */}
                        <div className="email-modal-meta">
                            <div className="modal-meta-item">
                                <span className="meta-label">From:</span>
                                <span className="meta-value">{modalEmail.senderEmail || "Unknown"}</span>
                            </div>
                            <div className="modal-meta-item">
                                <span className="meta-label">Date:</span>
                                <span className="meta-value">{modalEmail.createdAt ? new Date(modalEmail.createdAt).toLocaleString() : "Unknown"}</span>
                            </div>
                        </div>

                        {/* View Mode Toggle */}
                        <div className="email-modal-toggle">
                            <button
                                className={`toggle-btn ${modalViewMode === 'email' ? 'active' : ''}`}
                                onClick={() => setModalViewMode('email')}
                            >
                                <span className="toggle-icon">📧</span>
                                Email View
                            </button>
                            <button
                                className={`toggle-btn ${modalViewMode === 'text' ? 'active' : ''}`}
                                onClick={() => setModalViewMode('text')}
                            >
                                <span className="toggle-icon">📝</span>
                                Text View
                            </button>
                        </div>

                        {/* Modal Content */}
                        <div className="email-modal-content">
                            {loadingBodies[modalEmail.id] ? (
                                <div className="modal-loading">
                                    <div className="loading-spinner"></div>
                                    <p>Loading email content...</p>
                                </div>
                            ) : modalViewMode === 'email' ? (
                                <div className="modal-email-view">
                                    {(() => {
                                        const emailData = emailBodies[modalEmail.id] || modalEmail;
                                        const content = extractEmailContent(emailData);
                                        if (content.html) {
                                            return (
                                                <iframe
                                                    title="Email Content"
                                                    srcDoc={content.html}
                                                    className="email-iframe"
                                                    sandbox="allow-same-origin"
                                                />
                                            );
                                        }
                                        return <div className="no-html-content">No HTML content available</div>;
                                    })()}
                                </div>
                            ) : (
                                <div className="modal-text-view">
                                    <pre className="text-content">
                                        {(() => {
                                            const emailData = emailBodies[modalEmail.id] || modalEmail;
                                            const content = extractEmailContent(emailData);
                                            return content.text || "No text content available";
                                        })()}
                                    </pre>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

EmailListView.propTypes = {
    title: PropTypes.string,
    emails: PropTypes.array.isRequired,
    onMarkAsReviewed: PropTypes.func.isRequired,
    onAddComment: PropTypes.func.isRequired,
    onUpdateComment: PropTypes.func.isRequired,
    onViewInvoice: PropTypes.func,
    currentPage: PropTypes.number,
    totalPages: PropTypes.number,
    onPageChange: PropTypes.func,
    defaultExpanded: PropTypes.bool
};

EmailListView.defaultProps = {
    currentPage: 1,
    totalPages: 1,
    onPageChange: () => {},
    defaultExpanded: false
};

export default EmailListView;